/**
 * Rounding for numbers that are about to be sent to a Django DecimalField.
 *
 * WHY THIS EXISTS. Tom's signup failed six times on 2026-09-16 with
 * "Ensure that there are no more than 6 digits in total." Six laying hens at the grower
 * stage: 0.15 * 6 in JavaScript is 0.8999999999999999, seventeen digits posted into a
 * DecimalField(max_digits=6, decimal_places=2), and DRF rejected the whole request. It
 * had nothing to do with the life stage - any flock size whose rate lands on a repeating
 * binary fraction does the same thing, and 0.9 only looked like a stage problem because
 * the number that replaced it happened to round cleanly.
 *
 * So: round every COMPUTED decimal to the field's precision before it goes in a payload.
 * Values a customer typed are already the length they typed.
 */

/** Round to `places` decimals, matching the DecimalField's decimal_places.
 *
 * Not toFixed: 1.005 is really 1.00499999999999989 in binary, so toFixed(2) gives 1.00
 * and a half rounds the wrong way. The EPSILON nudge puts values that LOOK like a half
 * onto the half they look like. Negative values round toward zero at the half, which is
 * fine for the quantities this project stores (lbs, cups, money).
 */
export const toDecimalPlaces = (value: number, places = 2): number => {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** places;
  return Math.round((value + Number.EPSILON) * factor) / factor;
};

/** Two decimals: what almost every DecimalField in this project uses (lbs, cups, money). */
export const toTwoDecimals = (value: number): number => toDecimalPlaces(value, 2);
